import {
  useQuery, useMutation, useQueryClient 
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getSupplierPurchaseOrders,
  acknowledgePurchaseOrder,
  updatePOStatus,
  type POFilters,
} from '../api/orders-client';
import type { SupplierPOAction } from '../types/orders';

// Supplier ID will be retrieved from auth context in production
const MOCK_SUPPLIER_ID = 'sup-sysco';

export function usePurchaseOrders(filters?: Omit<POFilters, 'supplierId'>) {
  return useQuery({
    queryKey: ['supplier-purchase-orders', MOCK_SUPPLIER_ID, filters],
    queryFn: () => getSupplierPurchaseOrders(MOCK_SUPPLIER_ID, filters),
  });
}

export function useAcknowledgePO() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acknowledgePurchaseOrder,
    onSuccess: () => {
      toast.success('Purchase order acknowledged');
      queryClient.invalidateQueries({ queryKey: ['supplier-purchase-orders'] });
    },
    onError: (error) => {
      toast.error(`Failed to acknowledge PO: ${error.message}`);
    },
  });
}

export function useUpdatePOStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      poId, action 
    }: {
      poId: string;
      action: SupplierPOAction 
    }) =>
      updatePOStatus(poId, action),
    onSuccess: (_, { action }) => {
      const messages: Record<SupplierPOAction, string> = {
        acknowledge: 'Purchase order acknowledged',
        ship: 'Shipment confirmed',
        request_change: 'Change request submitted',
      };
      toast.success(messages[action]);
      queryClient.invalidateQueries({ queryKey: ['supplier-purchase-orders'] });
    },
    onError: (error) => {
      toast.error(`Failed to update PO: ${error.message}`);
    },
  });
}
